import * as React from 'react';
import {} from '@packages/systems/wfdl';
import {useExpressionEditorImplementationContext} from '@packages/systems/ExpressionEditor-utils';
import {styled, useTheme} from '@packages/utilities/styled';
import {Label, ImagePreview} from './LinkUIComponents';
import {Text} from '@packages/systems/design-system/Text';
import {Box} from '@packages/systems/design-system/Box';
import Button from '@packages/systems/design-system/Button';
import {Grid} from '@packages/systems/design-system/Grid';
import {isImageAsset} from '@packages/domains/assets/utilities';
import {getDocIcon} from '@packages/shared/utils/AssetUtils';
import {WarningXsmall as WarningIcon} from '@packages/systems/ui/icons';
import {HEAVY_BYTES} from '@packages/utilities/file-upload';
import {prettySize, truncateMid} from '@packages/utilities/string-utils';
import {IMAGE_PLACEHOLDER_FULL_ID} from '@packages/systems/plugins-utils/Asset';
import {LinkTarget} from './LinkTarget';
import type {State} from './LinkTypes';
import {useUploadFromDevice} from '../../../Asset/useUploadFromDevice';

const StyledWarning = styled(Box)(({theme}) => ({color: theme.colors.warning}));

export const useFile = (state: State, dispatch: (arg1?: any) => void) => {
  const {env} = useExpressionEditorImplementationContext();
  const [assetId = IMAGE_PLACEHOLDER_FULL_ID, setAssetId] = React.useState(
    state.assetId
  );
  const isSelecting = env.asset.isSelecting();
  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  const abortChoose = React.useCallback(env.asset.abortChoose, []);

  // Track whether we just initiated asset selection. When asset selection starts,
  // the Assets panel opens, which may cause the parent panel to close and unmount
  // this component. We should NOT abort the selection in this case since we
  // intentionally started it.
  const justInitiatedSelectionRef = React.useRef(false);

  React.useEffect(
    () => () => {
      if (isSelecting && !justInitiatedSelectionRef.current) {
        abortChoose();
      }
    },
    [abortChoose, isSelecting]
  );

  React.useEffect(() => setAssetId(state.assetId), [state.assetId]);

  const onChange = () => {
    justInitiatedSelectionRef.current = true;

    env.transaction.begin();

    env.asset.choose({
      type: 'all',
      onChange(chosenAsset: any) {
        setAssetId(chosenAsset._id);
        dispatch({payload: {...state, assetId: chosenAsset._id}});
      },
      onCancel() {
        justInitiatedSelectionRef.current = false;
        abortChoose();
      },
      onCommit() {
        justInitiatedSelectionRef.current = false;
        env.transaction.commit();
      },
    });
  };

  return {assetId, isSelecting, onChange};
};

const oneLineText = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

// @ts-expect-error - TS7031 - Binding element 'asset' implicitly has an 'any' type.
const PreviewContents = ({asset}) => {
  let previewContent;
  let metaContent;

  if (
    !asset ||
    isImageAsset(asset) ||
    // HACK: We don't set `mimeType` on our placeholder image asset, so we
    // need to explicitly treat it as an image here:
    asset._id === IMAGE_PLACEHOLDER_FULL_ID
  ) {
    const sourceFileName = asset ? asset.origFileName : '';
    const sourceSize = asset ? asset.fileSize : 0;
    const sourceWidth = asset ? asset.width : 0;
    const sourceHeight = asset ? asset.height : 0;
    const src = asset ? asset.thumbUrl || asset.cdnUrl || asset.s3Url : '';

    previewContent = src ? <ImagePreview src={src} alt="img preview" /> : null;
    metaContent = asset ? (
      <>
        <Text data-automation-id={sourceFileName} style={oneLineText}>
          {sourceFileName}
        </Text>
        <Text color="foregroundDim">
          {sourceWidth} x {sourceHeight}px
        </Text>
        <Text
          color="foregroundDim"
          display="inline-grid"
          gridAutoFlow="column"
          gridGap={1}
          alignItems="center"
          justifyContent="start"
        >
          {prettySize(sourceSize)}{' '}
          {sourceSize > HEAVY_BYTES ? (
            <StyledWarning
              title="This image file is very large, and will make your site loadtime slower. Try replacing it with a smaller image." // We should be using some kind of tooltip trigger instead
            >
              <WarningIcon />
            </StyledWarning>
          ) : null}
        </Text>
      </>
    ) : (
      <Text color="foregroundDim">No file chosen</Text>
    );
  } else {
    const PreviewIcon =
      asset && (asset.cdnUrl || asset.origFileName)
        ? getDocIcon(asset.cdnUrl || asset.origFileName, 'medium')
        : null;

    const assetName = asset ? truncateMid(asset.origFileName, 32) : null;
    const sizeText = asset ? prettySize(asset.fileSize) : null;

    previewContent = PreviewIcon && (
      <PreviewIcon style={{width: '100%', height: '100%'}} />
    );
    metaContent = (
      <>
        <Text style={oneLineText}>{assetName}</Text>
        {sizeText && <Text color="foregroundDim">{sizeText}</Text>}
      </>
    );
  }

  return (
    <Grid
      gridTemplateColumns="auto 1fr"
      gridColumn="1 / -1"
      gridRowGap={0}
      gridColumnGap={2}
      alignItems="center"
    >
      <Box
        width={48}
        height={48}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {previewContent}
      </Box>
      <Grid gridGap={1}>{metaContent}</Grid>
    </Grid>
  );
};

export const LinkFileMode = ({
  state,
  dispatch,
  isOverride,
  hasLegacyDynamoHrefBinding,
}: {
  state: State;
  dispatch: (arg1?: any) => void;
  isOverride: boolean;
  hasLegacyDynamoHrefBinding?: boolean;
}) => {
  const {env} = useExpressionEditorImplementationContext();
  const {assetId, isSelecting, onChange} = useFile(state, dispatch);
  const theme = useTheme();

  const {canUploadFromDevice, isUploading, chooseFromDevice} =
    useUploadFromDevice({
      accept: 'all',
      onUploaded(uploadedAsset) {
        dispatch({
          payload: {
            ...state,
            assetId: uploadedAsset._id,
            assetUrl: uploadedAsset.cdnUrl,
          },
        });
      },
    });

  const resolvedAsset = env.asset.get({type: 'all', id: assetId});
  // Device-uploaded CMS files aren't in the site asset store, so fall back to
  // the stored url for the preview and chosen-state.
  const asset =
    resolvedAsset ||
    (state.assetUrl
      ? {
          _id: assetId,
          origFileName: '',
          fileSize: 0,
          width: 0,
          height: 0,
          cdnUrl: state.assetUrl,
        }
      : null);
  const isAssetChosen = asset && asset._id !== IMAGE_PLACEHOLDER_FULL_ID;
  const label = isAssetChosen ? 'Replace' : 'Choose';
  const onChooseAsset = canUploadFromDevice ? chooseFromDevice : onChange;

  return (
    <>
      {/* Asset */}
      {hasLegacyDynamoHrefBinding ? null : (
        <>
          <Label
            data-automation-id="symbols-dataType-contentType"
            isOverride={isOverride}
          >
            Asset
          </Label>
          <Grid
            gridGap={2}
            style={{
              backgroundColor: theme.colors.backgroundVivid,
              borderColor: theme.colors.border,
              borderRadius: 2,
              borderStyle: 'solid',
              borderWidth: 1,
              padding: 4,
            }}
          >
            <PreviewContents asset={asset} />
            <Button
              data-automation-id="Type--Plugin_Asset_Asset"
              onClick={onChooseAsset}
              disabled={isSelecting || isUploading}
              compact
              style={{gridColumn: '1 / -1'}}
            >
              {isUploading ? 'Uploading…' : `${label} Asset...`}
            </Button>
          </Grid>
        </>
      )}
      {/* Target */}
      <Label
        data-automation-id="symbols-dataType-contentType"
        isOverride={isOverride}
      >
        Open in
      </Label>
      <LinkTarget state={state} dispatch={dispatch} />
    </>
  );
};
